import React, { useEffect, useState } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { useRef } from 'react';
import CosmicBackground from '../components/CosmicBackground';

const Contact = () => {
  const [scrollY, setScrollY] = useState(0);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { scrollYProgress } = useScroll();
  
  const y1 = useTransform(scrollYProgress, [0, 1], [0, -50]);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setShowSuccessMessage(false);
    setShowErrorMessage(false);

    // Check if we're in development (localhost) or production
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    try {
      // Get form data
      const formData = new FormData(e.target);
      
      // Add Web3Forms access key
      formData.append("access_key", "1e8be737-bf7d-4411-96de-80990187c3b8");

      if (isLocalhost) {
        // Development mode - simulate form submission for UI testing
        console.log('🔧 DEVELOPMENT MODE: Simulating Web3Forms submission');
        console.log('📧 Form data that would be sent:', Object.fromEntries(formData));
        
        // Simulate network delay
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Always show success in development
        setShowSuccessMessage(true);
        e.target.reset();
        
        setTimeout(() => setShowSuccessMessage(false), 5000);
      } else {
        // Production mode - actual Web3Forms submission
        const response = await fetch('https://api.web3forms.com/submit', {
          method: 'POST',
          body: formData
        });

        const result = await response.json();

        if (result.success) {
          setShowSuccessMessage(true);
          e.target.reset();
          setTimeout(() => setShowSuccessMessage(false), 5000);
        } else {
          console.log("Web3Forms Error", result);
          setShowErrorMessage(true);
          setTimeout(() => setShowErrorMessage(false), 5000);
        }
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setShowErrorMessage(true);
      setTimeout(() => setShowErrorMessage(false), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <CosmicBackground variant="contact">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2 }}
        className="min-h-screen relative z-10"
      >
        <div className="pt-32 pb-20">
          <div className="max-w-6xl mx-auto px-4">
            
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
              className="text-center mb-20"
            >
              <motion.h1
                className="text-3xl sm:text-4xl md:text-5xl font-bold mb-8 bg-gradient-to-r from-yellow-300 via-yellow-200 to-yellow-300 bg-clip-text text-transparent leading-tight py-2"
                animate={{
                  backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
                }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                }}
                style={{
                  backgroundSize: "200% 200%",
                }}
              >
                ✨ Contact Us ✨
              </motion.h1>
              <p className="text-lg text-gray-200 max-w-4xl mx-auto leading-relaxed">
                Ready to unlock the secrets of your cosmic blueprint? Get in touch for personalized astrological guidance.
              </p>
            </motion.div>

            <div className="grid lg:grid-cols-2 gap-16">
              
              {/* Leave A Message Form */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3, duration: 0.8 }}
                className="relative"
                style={{ y: y1 }}
              >
                <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl shadow-2xl p-8 border border-white/20">
                  {/* Cosmic Glow Effect */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-3xl"
                    animate={{
                      boxShadow: [
                        "0 0 30px rgba(168, 85, 247, 0.2)",
                        "0 0 60px rgba(236, 72, 153, 0.3)",
                        "0 0 30px rgba(168, 85, 247, 0.2)",
                      ],
                    }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                    }}
                  />
                  
                  {/* Floating Celestial Symbols */}
                  <div className="absolute top-4 right-4 flex space-x-1">
                    {[
                      { symbol: "⭐", color: "text-yellow-300", animation: "rotate" },
                      { symbol: "🌙", color: "text-blue-200", animation: "pulse" },
                      { symbol: "🌞", color: "text-orange-300", animation: "bounce" }
                    ].map((item, i) => (
                      <motion.div
                        key={i}
                        className={`w-4 h-4 ${item.color}`}
                        animate={
                          item.animation === "rotate" 
                            ? { rotate: [0, 360], scale: [1, 1.2, 1] }
                            : item.animation === "pulse"
                            ? { scale: [1, 1.3, 1], opacity: [0.7, 1, 0.7] }
                            : { y: [0, -8, 0], scale: [1, 1.1, 1] }
                        }
                        transition={{
                          duration: 3 + i,
                          repeat: Infinity,
                          delay: i * 0.3,
                        }}
                      >
                        {item.symbol}
                      </motion.div>
                    ))}
                  </div>
                  
                  <h2 className="text-3xl font-bold mb-8 text-white relative z-10 flex items-center">
                    <span className="text-3xl mr-4">✉️</span>
                    Leave A Message
                  </h2>
                  
                  {/* Success Message */}
                  {showSuccessMessage && (
                    <motion.div
                      initial={{ opacity: 0, y: -20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="mb-6 p-4 bg-green-500/20 border border-green-400/30 rounded-xl text-green-200 relative z-10"
                    >
                      <div className="flex items-center space-x-2">
                        <span className="text-xl">✅</span>
                        <span>Message sent successfully! Your email has been delivered to support@astrohelp.co.in. We'll get back to you within 24 hours.</span>
                      </div>
                    </motion.div>
                  )}

                  {/* Error Message */}
                  {showErrorMessage && (
                    <motion.div
                      initial={{ opacity: 0, y: -20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="mb-6 p-4 bg-red-500/20 border border-red-400/30 rounded-xl text-red-200 relative z-10"
                    >
                      <div className="flex items-center space-x-2">
                        <span className="text-xl">❌</span>
                        <span>Failed to send message. Please try again or contact us directly.</span>
                      </div>
                    </motion.div>
                  )}
                  
                  {/* Contact Form */}
                  <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
                    {/* Hidden Web3Forms fields */}
                    <input type="hidden" name="from_name" value="Astrohelp Contact Form" />
                    {/* Name Field */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4, duration: 0.6 }}
                    >
                      <label htmlFor="name" className="block text-sm font-medium text-gray-200 mb-2">
                        Full Name *
                      </label>
                      <motion.input
                        type="text"
                        id="name"
                        name="name"
                        required
                        whileFocus={{ scale: 1.02 }}
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent backdrop-blur-sm transition-all duration-300"
                        placeholder="Enter your full name"
                      />
                    </motion.div>

                    {/* Email Field */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5, duration: 0.6 }}
                    >
                      <label htmlFor="email" className="block text-sm font-medium text-gray-200 mb-2">
                        Email Address *
                      </label>
                      <motion.input
                        type="email"
                        id="email"
                        name="email"
                        required
                        whileFocus={{ scale: 1.02 }}
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent backdrop-blur-sm transition-all duration-300"
                        placeholder="Enter your email address"
                      />
                    </motion.div>

                    {/* Subject Field */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.6, duration: 0.6 }}
                    >
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-200 mb-2">
                        Subject *
                      </label>
                      <motion.input
                        type="text"
                        id="subject"
                        name="subject"
                        required
                        whileFocus={{ scale: 1.02 }}
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent backdrop-blur-sm transition-all duration-300"
                        placeholder="What's this regarding?"
                      />
                    </motion.div>

                    {/* Message Field */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.7, duration: 0.6 }}
                    >
                      <label htmlFor="message" className="block text-sm font-medium text-gray-200 mb-2">
                        Your Message *
                      </label>
                      <motion.textarea
                        id="message"
                        name="message"
                        required
                        rows="5"
                        whileFocus={{ scale: 1.02 }}
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent backdrop-blur-sm transition-all duration-300 resize-none"
                        placeholder="Tell us about your query or how we can help you..."
                      />
                    </motion.div>

                    {/* Submit Button */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.8, duration: 0.6 }}
                    >
                      <motion.button
                        type="submit"
                        disabled={isSubmitting}
                        whileHover={!isSubmitting ? { 
                          scale: 1.05,
                          boxShadow: "0 0 30px rgba(168, 85, 247, 0.5)"
                        } : {}}
                        whileTap={!isSubmitting ? { scale: 0.95 } : {}}
                        className={`w-full font-bold py-4 px-8 rounded-xl shadow-lg transition-all duration-300 relative overflow-hidden text-white hover:shadow-xl ${
                          isSubmitting 
                            ? 'bg-gradient-to-r from-gray-600 to-gray-700 cursor-not-allowed' 
                            : 'bg-gradient-to-r from-purple-600 to-pink-600'
                        }`}
                      >
                        {/* Button Background Animation */}
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-purple-400 to-pink-400 opacity-0 hover:opacity-100 transition-opacity duration-300"
                          animate={{
                            backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
                          }}
                          transition={{
                            duration: 3,
                            repeat: Infinity,
                          }}
                          style={{
                            backgroundSize: "200% 200%",
                          }}
                        />
                        <span className="relative z-10 flex items-center justify-center space-x-2">
                          {isSubmitting ? (
                            <>
                              <motion.span
                                animate={{ rotate: 360 }}
                                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                              >
                                ⏳
                              </motion.span>
                              <span>Sending...</span>
                            </>
                          ) : (
                            <>
                              <span>✨</span>
                              <span>Send Message</span>
                              <span>🚀</span>
                            </>
                          )}
                        </span>
                      </motion.button>
                    </motion.div>
                  </form>
                  
                  {/* Form Footer */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.9, duration: 0.6 }}
                    className="mt-6 p-4 bg-white/5 rounded-xl relative z-10"
                  >
                    <p className="text-sm text-gray-300 text-center">
                      💫 We'll get back to you within 24 hours!
                    </p>
                  </motion.div>
                  
                  {/* Background Pattern */}
                  <div className="absolute inset-0 opacity-5">
                    {[...Array(15)].map((_, i) => (
                      <motion.div
                        key={i}
                        className="absolute w-1 h-1 bg-white rounded-full"
                        style={{
                          left: `${Math.random() * 100}%`,
                          top: `${Math.random() * 100}%`,
                        }}
                        animate={{
                          opacity: [0.2, 1, 0.2],
                          scale: [0.5, 1, 0.5],
                        }}
                        transition={{
                          duration: 2 + Math.random() * 3,
                          repeat: Infinity,
                          delay: Math.random() * 2,
                        }}
                      />
                    ))}
                  </div>
                </div>
              </motion.div>

              {/* Contact Information */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5, duration: 0.8 }}
                className="space-y-8"
              >
                {/* Contact Details */}
                <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl shadow-2xl p-8 border border-white/20">
                  {/* Cosmic Glow Effect */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-br from-green-500/10 to-teal-500/10 rounded-3xl"
                    animate={{
                      boxShadow: [
                        "0 0 30px rgba(34, 197, 94, 0.2)",
                        "0 0 60px rgba(20, 184, 166, 0.3)",
                        "0 0 30px rgba(34, 197, 94, 0.2)",
                      ],
                    }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                    }}
                  />
                  
                  <h3 className="text-2xl font-bold mb-6 text-white flex items-center relative z-10">
                    <span className="text-3xl mr-4">📞</span>
                    Contact Details
                  </h3>
                  <div className="space-y-4 relative z-10">
                    <motion.a
                      href="tel:+919586074349"
                      whileHover={{ scale: 1.05 }}
                      className="flex items-center space-x-4 text-lg text-gray-200 hover:text-yellow-300 transition-colors duration-300"
                    >
                      <span className="text-2xl">📱</span>
                      <span>+91-9586074349</span>
                    </motion.a>
                    
                    <motion.a
                      href="mailto:info@astrohelp.co.in"
                      whileHover={{ scale: 1.05 }}
                      className="flex items-center space-x-4 text-lg text-gray-200 hover:text-yellow-300 transition-colors duration-300"
                    >
                      <span className="text-2xl">✉️</span>
                      <span>info@astrohelp.co.in</span>
                    </motion.a>
                  </div>
                </div>

                {/* YouTube Channel */}
                <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl shadow-2xl p-8 border border-white/20">
                  {/* Cosmic Glow Effect */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-br from-red-500/10 to-pink-500/10 rounded-3xl"
                    animate={{
                      boxShadow: [
                        "0 0 30px rgba(239, 68, 68, 0.2)",
                        "0 0 60px rgba(236, 72, 153, 0.3)",
                        "0 0 30px rgba(239, 68, 68, 0.2)",
                      ],
                    }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                    }}
                  />
                  
                  <h3 className="text-2xl font-bold mb-6 text-white flex items-center relative z-10">
                    <span className="text-3xl mr-4">📺</span>
                    YouTube Channel
                  </h3>
                  <motion.a
                    href="https://www.youtube.com/channel/UCatQyC2amIuqm8DnIUtpgfA"
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.05 }}
                    className="flex items-center space-x-4 text-lg text-gray-200 hover:text-red-400 transition-colors duration-300 relative z-10"
                  >
                    <span className="text-2xl">🎥</span>
                    <div>
                      <div className="font-semibold">Avantika's Astrohelp</div>
                      <div className="text-sm text-gray-400">14.7K subscribers</div>
                    </div>
                  </motion.a>
                </div>

                {/* Working Hours */}
                <div className="relative bg-gradient-to-br from-purple-500/20 to-pink-500/20 backdrop-blur-xl rounded-3xl shadow-2xl p-8 border border-purple-400/30">
                  {/* Cosmic Glow Effect */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-br from-purple-600/10 to-pink-600/10 rounded-3xl"
                    animate={{
                      boxShadow: [
                        "0 0 30px rgba(168, 85, 247, 0.3)",
                        "0 0 60px rgba(236, 72, 153, 0.4)",
                        "0 0 30px rgba(168, 85, 247, 0.3)",
                      ],
                    }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                    }}
                  />
                  
                  <h3 className="text-2xl font-bold mb-6 text-white flex items-center relative z-10">
                    <span className="text-3xl mr-4">⏰</span>
                    Consultation Hours
                  </h3>
                  <div className="space-y-3 text-lg text-gray-200 relative z-10">
                    <div className="flex justify-between">
                      <span>Monday - Friday:</span>
                      <span>10:00 AM - 8:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Saturday:</span>
                      <span>10:00 AM - 6:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Sunday:</span>
                      <span>2:00 PM - 6:00 PM</span>
                    </div>
                  </div>
                  
                  <motion.div
                    animate={{
                      y: [0, -5, 0],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                    }}
                    className="mt-6 p-4 bg-white/20 rounded-xl relative z-10"
                  >
                    <p className="text-sm text-gray-200">
                      💫 All consultations are conducted via phone call for your convenience
                    </p>
                  </motion.div>
                </div>
              </motion.div>
            </div>

            {/* Interactive Map Section */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mt-20"
            >
              <h2 className="text-4xl font-bold text-center mb-12 text-white">
                Find Us Here
              </h2>
              <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl shadow-2xl p-8 border border-white/20">
                {/* Cosmic Glow Effect */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 to-blue-500/10 rounded-3xl"
                  animate={{
                    boxShadow: [
                      "0 0 30px rgba(99, 102, 241, 0.2)",
                      "0 0 60px rgba(59, 130, 246, 0.3)",
                      "0 0 30px rgba(99, 102, 241, 0.2)",
                    ],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                  }}
                />
                
                {/* Interactive Google Maps Embed */}
                <div className="aspect-video rounded-2xl overflow-hidden relative z-10">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3783.2110839344864!2d73.76896211490293!3d18.5555447!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc2bf2e67461101%3A0xa5a6e4b76e3b8ad2!2sFlat%20001%2C%20Sri%20Sai%20Chaitanya%2C%2092%2F1%2C%20Pancard%20Club%20Sus%20Road%2C%20opposite%20Murkute%20Garden%2C%20Baner%2C%20Pune%2C%20Maharashtra%20411045!5e0!3m2!1sen!2sin!4v1703947829814!5m2!1sen!2sin"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Astrohelp Office Location"
                    className="rounded-2xl"
                  ></iframe>
                </div>

                {/* Location Details Below Map */}
                <div className="mt-6 p-6 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 relative z-10">
                  <div className="flex items-start space-x-4">
                    <div className="text-3xl">📍</div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">Office Address:</h3>
                      <p className="text-gray-200 leading-relaxed">
                        Flat 001, Sri Sai Chaitanya<br />
                        92/1, Pancard Club Sus Road<br />
                        opposite Murkute Garden<br />
                        Baner, Pune 411045, Maharashtra, India
                      </p>
                      
                      <div className="mt-4 flex flex-wrap gap-4">
                        <motion.a
                          href="https://maps.google.com/?q=Flat+001,+Sri+Sai+Chaitanya,+92/1,+Pancard+Club+Sus+Road,+opposite+Murkute+Garden,+Baner,+Pune+411045"
                          target="_blank"
                          rel="noopener noreferrer"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="inline-flex items-center space-x-2 bg-blue-600/20 hover:bg-blue-600/30 text-blue-200 px-4 py-2 rounded-xl border border-blue-400/30 hover:border-blue-400 transition-all duration-300"
                        >
                          <span>🗺️</span>
                          <span>Open in Google Maps</span>
                        </motion.a>
                        
                        <motion.a
                          href="https://maps.google.com/?q=Flat+001,+Sri+Sai+Chaitanya,+92/1,+Pancard+Club+Sus+Road,+opposite+Murkute+Garden,+Baner,+Pune+411045&navigate=yes"
                          target="_blank"
                          rel="noopener noreferrer"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="inline-flex items-center space-x-2 bg-green-600/20 hover:bg-green-600/30 text-green-200 px-4 py-2 rounded-xl border border-green-400/30 hover:border-green-400 transition-all duration-300"
                        >
                          <span>🧭</span>
                          <span>Get Directions</span>
                        </motion.a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </CosmicBackground>
  );
};

export default Contact;
